import type { EChartsCoreOption } from 'echarts';

import { DF_STAGE_META } from '../../../core/df/df.types';
import { KS_STAGE_META } from '../../../core/kickstart/kickstart.types';
import {
  OpsAiRecommendation,
  OpsIntake,
  OpsStatusBreakdown,
} from '../../../core/ops/ops-summary.types';
import { aiRecommendationLabel } from '../../../core/status/status-meta.types';
import { axisTheme, chartPalette } from '../../../shared/chart/chart';

/**
 * The Ops dashboard's chart options as pure functions of the summary and the
 * theme — nothing here reads a signal or a service, so each can be checked
 * without a TestBed.
 */

/** Format an ISO date string "YYYY-MM-DD" as "DD MMM". */
function fmtDay(iso: string): string {
  const d = new Date(iso + 'T00:00:00');
  return d.toLocaleDateString('en-ZA', { day: '2-digit', month: 'short' });
}

const AI_COLORS: Record<OpsAiRecommendation, string> = {
  APPROVE: '#10b981',
  MORE_INFO: '#f59e0b',
  DECLINE: '#ef4444',
};

export function emptyChart(dark: boolean): EChartsCoreOption {
  const ax = axisTheme(dark);
  return {
    title: {
      text: 'No data yet',
      left: 'center',
      top: 'center',
      textStyle: { color: ax.axis, fontSize: 13, fontWeight: 'normal' },
    },
    series: [],
  };
}

/** AI recommendation mix (donut). */
export function aiMixChart(
  recs: Partial<Record<OpsAiRecommendation, number>>,
  dark: boolean,
): EChartsCoreOption {
  const ax = axisTheme(dark);

  const order: OpsAiRecommendation[] = ['APPROVE', 'MORE_INFO', 'DECLINE'];
  const entries = order.map((k) => [k, recs[k] ?? 0] as const).filter(([, v]) => v > 0);

  if (!entries.length) return emptyChart(dark);

  const total = entries.reduce((sum, [, v]) => sum + v, 0);

  return {
    color: entries.map(([k]) => AI_COLORS[k]),
    tooltip: {
      trigger: 'item',
      backgroundColor: ax.tooltipBg,
      borderColor: ax.split,
      textStyle: { color: ax.tooltipText },
      formatter: '{b}: {c} ({d}%)',
    },
    legend: {
      orient: 'vertical',
      right: 0,
      top: 'center',
      textStyle: { color: ax.axis, fontSize: 11 },
      itemWidth: 10,
      itemHeight: 10,
    },
    graphic: {
      type: 'text',
      left: '37%',
      top: 'center',
      style: {
        text: `${total}\nin review`,
        textAlign: 'center',
        fill: ax.tooltipText,
        fontSize: 13,
        fontWeight: 600,
        lineHeight: 18,
      },
    },
    series: [
      {
        name: 'AI advisory',
        type: 'pie',
        radius: ['48%', '70%'],
        center: ['38%', '50%'],
        avoidLabelOverlap: true,
        label: { show: false },
        emphasis: { label: { show: false } },
        data: entries.map(([k, v]) => ({ name: aiRecommendationLabel(k), value: v })),
      },
    ],
  };
}

/** Application stage mix (donut), KS and DF merged under one label. */
export function stageMixChart(
  sb: OpsStatusBreakdown | undefined,
  dark: boolean,
): EChartsCoreOption {
  const palette = chartPalette();
  const ax = axisTheme(dark);

  if (!sb) return emptyChart(dark);

  // Merge KS + DF stage counts under a shared human label.
  const merged: Record<string, number> = {};
  for (const [key, count] of Object.entries(sb.ks_stage)) {
    const label = KS_STAGE_META[key as keyof typeof KS_STAGE_META]?.label ?? key;
    merged[label] = (merged[label] ?? 0) + count;
  }
  for (const [key, count] of Object.entries(sb.df_stage)) {
    const label = DF_STAGE_META[key as keyof typeof DF_STAGE_META]?.label ?? key;
    merged[label] = (merged[label] ?? 0) + count;
  }
  const entries = Object.entries(merged).filter(([, v]) => v > 0);
  if (!entries.length) return emptyChart(dark);

  return {
    color: palette,
    tooltip: {
      trigger: 'item',
      backgroundColor: ax.tooltipBg,
      borderColor: ax.split,
      textStyle: { color: ax.tooltipText },
      formatter: '{b}: {c} ({d}%)',
    },
    legend: {
      orient: 'vertical',
      right: 0,
      top: 'center',
      textStyle: { color: ax.axis, fontSize: 11 },
      itemWidth: 10,
      itemHeight: 10,
    },
    series: [
      {
        name: 'Stage',
        type: 'pie',
        radius: ['48%', '70%'],
        center: ['38%', '50%'],
        avoidLabelOverlap: true,
        label: { show: false },
        emphasis: { label: { show: false } },
        data: entries.map(([name, value]) => ({ name, value })),
      },
    ],
  };
}

/** Intake (bar, KS vs DF). */
export function intakeChart(intake: OpsIntake | undefined, dark: boolean): EChartsCoreOption {
  const palette = chartPalette();
  const ax = axisTheme(dark);

  if (!intake?.days?.length) return emptyChart(dark);

  return {
    color: [palette[0], palette[1]],
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      backgroundColor: ax.tooltipBg,
      borderColor: ax.split,
      textStyle: { color: ax.tooltipText },
    },
    legend: {
      data: ['Kickstart Cash', 'Debtors Finance'],
      textStyle: { color: ax.axis },
      right: 0,
      top: 0,
    },
    grid: { left: 0, right: 8, bottom: 0, top: 40, containLabel: true },
    xAxis: {
      type: 'category',
      data: intake.days.map(fmtDay),
      axisLine: { lineStyle: { color: ax.split } },
      axisLabel: { color: ax.axis, fontSize: 10 },
      axisTick: { show: false },
    },
    yAxis: {
      type: 'value',
      splitLine: { lineStyle: { color: ax.split } },
      axisLabel: { color: ax.axis, fontSize: 10 },
      axisLine: { show: false },
      axisTick: { show: false },
      minInterval: 1,
    },
    series: [
      {
        name: 'Kickstart Cash',
        type: 'bar',
        stack: 'intake',
        data: intake.ks,
        barMaxWidth: 26,
      },
      {
        name: 'Debtors Finance',
        type: 'bar',
        stack: 'intake',
        data: intake.df,
        barMaxWidth: 26,
        itemStyle: { borderRadius: [3, 3, 0, 0] },
      },
    ],
  };
}
